import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ProjectService } from 'src/app/shared/project/project.service';
import { ProjectListItem, ProjectListModel } from '../shared/project/project-list-item';
import { ImportProgressService } from 'src/app/shared/import/import-progress.service';
import { DataService } from '../shared/network/data.service';
import { ExampleArticleItem } from '../import/upload/example-article-item';
import { UploadFileModalComponent } from '../import/upload/upload-file-modal.component';
import { ImportStep } from '../import/import-step';
import { TagItem } from '../shared/project/tag-item';

@Component({
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss'],
})
export class ProjectListComponent implements OnInit {
    ImportStep: typeof ImportStep = ImportStep;   //hack for enum to be available in the template
    projects: ProjectListItem[] = [];
    allTags: TagItem[] = [];
    selectedTags: string[] = [];
    projectNameFilter: string = '';
    exampleArticles: ExampleArticleItem[] = undefined;
    public articlesFilterStr = '';
    private articlesDisplayCount = 4;
    private tagFilterChanged = new Subject<void>();
    private nameFilterChanged = new Subject<void>();

    dataLoaded = false;
    public loading = false;
    private filterPending = false;

    constructor(private projectService: ProjectService, private importProgressService: ImportProgressService, private dataService: DataService,
        public dialog: MatDialog, private route: ActivatedRoute, private router: Router) {}

    ngOnInit(): void {
        this.loadProjects();
        this.importProgressService.clearImportStep();

        this.tagFilterChanged.pipe(debounceTime(800)).subscribe(() => {
            this.filterPending = false;
            this.loadProjects();
        });

        this.nameFilterChanged.pipe(debounceTime(800)).subscribe(() => {
            this.filterPending = false;
            this.loadProjects();
        });

        this.route.queryParams.subscribe(params => {
            const reuploadProjectId = params['reuploadProjectId'];
            if (reuploadProjectId) {
                this.showEditProjectDialog(reuploadProjectId);
            }
        });

        let self = this;
        this.dataService.get<ExampleArticleItem[]>('articles/examples').then(items => {
            self.exampleArticles = items;
        });
    }

    async deleteProject(project: ProjectListItem) {
        await this.projectService.deleteProject(project.id);
        const i = this.projects.indexOf(project, 0);
        if (i > -1) {
            this.projects.splice(i, 1);
        }
    }

    async duplicateProject(project: ProjectListItem) {
        await this.projectService.duplicateProject(project.id);
        this.loadProjects();
    }

    private async refreshAllTags(): Promise<void> {
        this.allTags = await this.projectService.getAllTags();
    }

    public loadProjects(): void {
        if (this.loading) {
            return;
        }
        this.loading = true;
        this.projectService.getProjectsList(0, this.selectedTags, this.projectNameFilter).then((res: ProjectListModel) => {
            this.loadUrls(res.projects);
            this.projects = res.projects;
            this.allTags = res.allTags;
            this.dataLoaded = true;
            this.loading = false;
        });
    }

    public loadMoreProjects(): void {
        if (this.loading) {
            return;
        }
        this.loading = true;
        let skip = this.projects.length;
        this.projectService.getProjectsList(skip, this.selectedTags, this.projectNameFilter).then((res: ProjectListModel) => {
            this.loadUrls(res.projects);
            this.projects.push(...res.projects);
            this.allTags = res.allTags;
            this.dataLoaded = true;
            this.loading = false;
        });
    }

    private loadUrls(items: ProjectListItem[]): void {
        for (let i = 0; i < items.length; ++i) {
            items[i].url = this.getUrlForProject(items[i]);
            items[i].downloadDwpUrl =  this.dataService.getBaseUrl() + `/projects/export/${items[i].id}`;
            items[i].downloadXlsxUrl =  this.dataService.getBaseUrl() + `/projects/downloadXlsx/${items[i].id}`;
        }
    }

    private getUrlForProject(proj: ProjectListItem): string {
        switch (proj.currentStep) {
            case ImportStep.Cards:
                return `/cards?projectId=${proj.id}`;
            default:
                return `/import-wizard?projectId=${proj.id}&step=${ImportStep[proj.currentStep]}`;
        }
    }

    public gotoProject(proj: ProjectListItem): void {
        this.router.navigateByUrl(this.getUrlForProject(proj));
    }

    showNewProjectDialog() {
        const dialogRef = this.dialog.open(UploadFileModalComponent, {
            data: { allTags: this.allTags }
        });
        const self = this;
        dialogRef.componentInstance.projectAdded.subscribe(async (projectId: string) => {
            let projListItem = await self.projectService.getProjectListItem(projectId);
            self.projects.unshift(projListItem);
            self.refreshAllTags();
        });
        dialogRef.componentInstance.tagDeleted.subscribe((tagName: string) => {
            self.refreshAllTags();
            self.projects.forEach(project => {
                if (project.tags) {
                    const projectTagIndex = project.tags.indexOf(tagName);
                    if (projectTagIndex >= 0) {
                        project.tags.splice(projectTagIndex, 1);
                    }
                }
            });
        });
    }

    showEditProjectDialog(projectId: string): void {
        const dialogRef = this.dialog.open(UploadFileModalComponent, {
            data: { projectId: projectId, allTags: this.allTags }
        });

        const self = this;
        dialogRef.componentInstance.tagDeleted.subscribe((tagName: string) => {
            self.refreshAllTags();
            // Remove tag from all projects
            self.projects.forEach(project => {
                if (project.tags) {
                    const projectTagIndex = project.tags.indexOf(tagName);
                    if (projectTagIndex >= 0) {
                        project.tags.splice(projectTagIndex, 1);
                    }
                }
            });
        });

        dialogRef.afterClosed().subscribe(async (changesApplied: boolean) => {
            if (changesApplied) {
                const updatedProject = await this.projectService.getProjectListItem(projectId);
                const index = this.projects.findIndex(proj => proj.id === projectId);
                if (index !== -1) {
                    this.projects[index] = updatedProject;
                }
                self.refreshAllTags();
            }
        });
    }

    public showMoreArticlesClick() {
        this.articlesDisplayCount += 4;
    }

    get filteredArticles(): ExampleArticleItem[] {
        return this.exampleArticles.filter(article => article.title.toUpperCase().includes(this.articlesFilterStr.toUpperCase()))
            .slice(0, this.articlesDisplayCount);
    }

    public toggleTagFilter(tagName: string): void {
        const index = this.selectedTags.indexOf(tagName);
        if (index > -1) {
            this.selectedTags.splice(index, 1);
        } else {
            this.selectedTags.push(tagName);
        }
        this.filterPending = true;
        this.tagFilterChanged.next();
    }

    public onNameFilterChange(): void {
        this.filterPending = true;
        this.nameFilterChanged.next();
    }

    public isTagSelected(tagName: string): boolean {
        return this.selectedTags.indexOf(tagName) > -1;
    }

    public isTagFilteringApplied(): boolean {
        return this.selectedTags.length > 0;
    }

    public isNameFilteringApplied(): boolean {
        return this.projectNameFilter && this.projectNameFilter.trim().length > 0;
    }

    public isFilteringApplied(): boolean {
        return this.isTagFilteringApplied() || this.isNameFilteringApplied();
    }
    
    public noProjectsAtAll(): boolean {
        return this.dataLoaded && this.projects && this.projects.length == 0 && !this.isFilteringApplied() && !this.loading && !this.filterPending;
    }
    
}
